import React, { useCallback, useEffect, useRef, useState } from 'react';
import crmClient from './crmClient';
import RichTextEditor from './RichTextEditor';

const emptyForm = { name: '', template_kind: 'marketing', audience: 'customers', subject: '', body: '', body_html: '' };

const DEFAULT_MERGE_FIELDS = [
  { token: '{{first_name}}', label: 'Contact first name' },
  { token: '{{full_name}}', label: 'Contact full name' },
  { token: '{{company}}', label: 'Company name' },
  { token: '{{last_order_date}}', label: 'Last order date' },
  { token: '{{lifetime_orders}}', label: 'Total orders' },
  { token: '{{lifetime_revenue}}', label: 'Lifetime revenue' },
  { token: '{{dashboard_url}}', label: 'Dashboard link' }
];

const TemplatesView = ({ meta }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const rteRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    crmClient
      .listTemplates()
      .then(payload => {
        setTemplates((payload && payload.templates) || []);
        setLoading(false);
      })
      .catch(() => {
        setError('Unable to load templates.');
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Debounced live preview of the branded email as it will actually send.
  useEffect(() => {
    if (!form.subject && !form.body && !form.body_html) {
      setPreview(null);
      return undefined;
    }
    const timer = setTimeout(() => {
      crmClient
        .previewTemplate({ subject: form.subject, body: form.body, body_html: form.body_html })
        .then(payload => setPreview((payload && payload.preview) || null))
        .catch(() => setPreview(null));
    }, 400);
    return () => clearTimeout(timer);
  }, [form.subject, form.body, form.body_html]);

  const audiences = (meta && meta.audiences) || ['customers', 'drivers', 'investors'];
  const mergeFields = (meta && meta.merge_fields) || DEFAULT_MERGE_FIELDS;

  // Insert HTML/text into the rich-text body at the cursor position.
  const insertIntoHtml = snippet => {
    if (rteRef.current) {
      rteRef.current.insertHtml(snippet);
    } else {
      setForm(f => ({ ...f, body_html: (f.body_html || '') + snippet }));
    }
  };

  const insertButton = () => {
    insertIntoHtml(
      '<p style="margin:0 0 22px;"><a href="{{dashboard_url}}" style="display:inline-block;background:#F15A29;color:#ffffff;text-decoration:none;font-weight:bold;padding:12px 22px;border-radius:8px;font-size:15px;">Open your dashboard</a></p>'
    );
  };

  const save = () => {
    setBusy(true);
    setError(null);
    const action = editingId ? crmClient.updateTemplate(editingId, form) : crmClient.createTemplate(form);
    action
      .then(() => {
        setBusy(false);
        setForm(emptyForm);
        setEditingId(null);
        load();
      })
      .catch(err => {
        setBusy(false);
        setError((err && err.response && err.response.data && err.response.data.message) || 'Unable to save template.');
      });
  };

  const edit = template => {
    setEditingId(template.crm_email_template_id);
    setForm({
      name: template.name || '',
      template_kind: template.template_kind || 'marketing',
      audience: template.audience || 'customers',
      subject: template.subject || '',
      body: template.body || '',
      body_html: template.body_html || ''
    });
  };

  const remove = id => {
    setBusy(true);
    crmClient
      .deleteTemplate(id)
      .then(() => {
        setBusy(false);
        if (editingId === id) {
          setEditingId(null);
          setForm(emptyForm);
        }
        load();
      })
      .catch(() => setBusy(false));
  };

  return (
    <div className="crm-templates">
      <div className="crm-card">
        <h3>{editingId ? 'Edit template' : 'New email template'}</h3>
        {error && <div className="crm-inline-error">{error}</div>}
        <div className="crm-field-row">
          <label className="crm-field-grow">
            <span>Name</span>
            <input value={form.name} onChange={event => setForm({ ...form, name: event.target.value })} />
          </label>
          <label>
            <span>Kind</span>
            <select value={form.template_kind} onChange={event => setForm({ ...form, template_kind: event.target.value })}>
              <option value="marketing">Marketing</option>
              <option value="automated">Automated</option>
            </select>
          </label>
          <label>
            <span>Audience</span>
            <select value={form.audience} onChange={event => setForm({ ...form, audience: event.target.value })}>
              {audiences.map(a => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </label>
        </div>
        {form.template_kind === 'automated' && (
          <p className="crm-hint">
            Tip: name this <code>onboarding</code>, <code>day3_followup</code>, <code>reengagement</code>, or <code>winback</code> to override that
            automated sequence email.
          </p>
        )}
        <label className="crm-field-grow">
          <span>Subject</span>
          <input value={form.subject} onChange={event => setForm({ ...form, subject: event.target.value })} />
        </label>

        <div className="crm-template-toolbar">
          <span className="crm-toolbar-label">Insert:</span>
          <button type="button" className="crm-btn crm-btn--ghost crm-btn--sm" onClick={insertButton}>
            Button
          </button>
          {mergeFields.map(field => (
            <button key={field.token} type="button" className="crm-chip" title={field.label} onClick={() => insertIntoHtml(field.token)}>
              {field.token}
            </button>
          ))}
        </div>

        {/* Not a <label>: wrapping a contentEditable in <label> collapses the caret on click. */}
        <div className="crm-field-grow crm-rte-field">
          <span>Body (formatted — paragraphs, links, images, attachments)</span>
          <RichTextEditor
            ref={rteRef}
            value={form.body_html}
            onChange={html => setForm(f => ({ ...f, body_html: html }))}
            onUpload={file => crmClient.uploadAsset(file)}
          />
        </div>

        <label className="crm-field-grow">
          <span>Plain-text fallback (shown if HTML can't render)</span>
          <textarea rows={4} value={form.body} onChange={event => setForm({ ...form, body: event.target.value })} />
        </label>

        <div className="crm-detail-actions">
          <button type="button" className="crm-btn" disabled={busy || !form.name} onClick={save}>
            {editingId ? 'Save changes' : 'Create template'}
          </button>
          {editingId && (
            <button
              type="button"
              className="crm-btn crm-btn--ghost"
              onClick={() => {
                setEditingId(null);
                setForm(emptyForm);
              }}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      <div className="crm-card">
        <h3>Live preview</h3>
        {preview ? (
          <div className="crm-email-preview">
            <div className="crm-email-preview-subject">
              <strong>Subject:</strong> {preview.subject || '—'}
            </div>
            <iframe title="Email preview" className="crm-email-preview-frame" srcDoc={preview.html} />
          </div>
        ) : (
          <p className="crm-empty">Start typing a subject or body to see the branded email.</p>
        )}
      </div>

      <div className="crm-table-wrap">
        <table className="crm-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Kind</th>
              <th>Audience</th>
              <th>Subject</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={5} className="crm-empty">
                  Loading templates…
                </td>
              </tr>
            )}
            {!loading && templates.length === 0 && (
              <tr>
                <td colSpan={5} className="crm-empty">
                  No templates yet.
                </td>
              </tr>
            )}
            {!loading &&
              templates.map(template => (
                <tr key={template.crm_email_template_id}>
                  <td>
                    <strong>{template.name}</strong>
                  </td>
                  <td>{template.template_kind}</td>
                  <td>{template.audience}</td>
                  <td>{template.subject || '—'}</td>
                  <td className="crm-num">
                    <button type="button" className="crm-btn crm-btn--ghost crm-btn--sm" onClick={() => edit(template)}>
                      Edit
                    </button>{' '}
                    <button type="button" className="crm-btn crm-btn--danger crm-btn--sm" disabled={busy} onClick={() => remove(template.crm_email_template_id)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TemplatesView;
